import { useEffect } from 'react'
import { PhoneOff, PhoneIncoming } from 'lucide-react'
import { useLanguage } from '@/lib/i18n/LanguageContext'
import { formatCallCopy } from './CallButton'

export interface IncomingCall {
  callId: string
  fromUserId: string
  fromName?: string
  pricePerMinute: number
  withVideo: boolean
}

interface IncomingCallOverlayProps {
  call: IncomingCall | null
  onAccept: (callId: string) => void
  onReject: (callId: string) => void
  /** Ring expiry: the backend drops unanswered invites after 45s. */
  ringTimeoutMs?: number
  onMissed?: (callId: string) => void
}

export const RING_TIMEOUT_MS = 45_000

/**
 * Global incoming-call sheet (mounted once, next to the snackbar host).
 * Shows the caller plus the price; accept joins the room and fetches the
 * token via the provider, reject hangs up. An unanswered ring reports
 * `onMissed` after `ringTimeoutMs`.
 */
export function IncomingCallOverlay({ call, onAccept, onReject, ringTimeoutMs = RING_TIMEOUT_MS, onMissed }: IncomingCallOverlayProps) {
  const { strings } = useLanguage()
  const copy = strings.calls

  useEffect(() => {
    if (!call || !onMissed) return
    const id = call.callId
    const timer = setTimeout(() => onMissed(id), ringTimeoutMs)
    return () => clearTimeout(timer)
  }, [call, ringTimeoutMs, onMissed])

  if (!call) return null

  const price = call.pricePerMinute <= 0 ? copy.incomingFree : formatCallCopy(copy.incomingRate, call.pricePerMinute)
  const title = call.withVideo ? copy.incomingVideoTitle : copy.incomingTitle

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 px-6 pb-[env(safe-area-inset-bottom)]" role="dialog" aria-label={title}>
      <div className="mb-8 w-full max-w-sm rounded-[20px] bg-lm-surface-container p-5 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-lm-primary text-lm-on-primary">
          <PhoneIncoming size={26} />
        </div>
        <h2 className="mt-3 text-headline-sm font-bold text-lm-on-surface">{title}</h2>
        <p className="mt-1 text-title-md text-lm-on-surface">{call.fromName ?? call.fromUserId.slice(0, 8)}</p>
        <p className="mt-1 text-body-md text-lm-on-surface-variant">{price}</p>
        <div className="mt-4 flex gap-3">
          <button
            type="button"
            onClick={() => onReject(call.callId)}
            className="flex h-12 flex-1 items-center justify-center gap-2 rounded-full bg-lm-error-container text-title-sm font-semibold text-lm-on-error-container"
          >
            <PhoneOff size={18} />
            {copy.reject}
          </button>
          <button
            type="button"
            onClick={() => onAccept(call.callId)}
            className="flex h-12 flex-1 items-center justify-center gap-2 rounded-full bg-lm-primary text-title-sm font-semibold text-lm-on-primary"
          >
            <PhoneIncoming size={18} />
            {copy.accept}
          </button>
        </div>
      </div>
    </div>
  )
}
